import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Select, message, Spin, Typography, Card, Divider } from 'antd';
import { SendOutlined, ReloadOutlined } from '@ant-design/icons';
import TextArea from 'antd/es/input/TextArea';
import { useNavigate } from 'react-router-dom';
import { mockDataService } from '../../services/mockDataService';
import { handleFormValidation } from '../../utils/validations';
import { LIMITS } from '../../utils/utils';
import '../../styles/TransferForm.css';

const { Title, Text } = Typography;
const { Option } = Select;

const TransferForm = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [savingsAccount, setSavingsAccount] = useState('');
  const [mortgageAccount, setMortgageAccount] = useState('');
  const [amount, setAmount] = useState('');
  const [remarks, setRemarks] = useState('');

  useEffect(() => {
    mockDataService.getAccounts().then((data) => {
      setAccounts(data);
      const sav = data.find((a) => a.type === 'Savings');
      const mort = data.find((a) => a.type === 'Mortgage');
      if (sav) setSavingsAccount(sav.accountNumber);
      if (mort) setMortgageAccount(mort.accountNumber);
      setLoadingAccounts(false);
    });
  }, []);

  const savingsAccounts = accounts.filter((a) => a.type === 'Savings');
  const mortgageAccounts = accounts.filter((a) => a.type === 'Mortgage');
  const selectedSavings = accounts.find((a) => a.accountNumber === savingsAccount);
  const selectedMortgage = accounts.find((a) => a.accountNumber === mortgageAccount);

  const handleReset = () => {
    setAmount('');
    setRemarks('');
    const sav = accounts.find((a) => a.type === 'Savings');
    const mort = accounts.find((a) => a.type === 'Mortgage');
    if (sav) setSavingsAccount(sav.accountNumber);
    if (mort) setMortgageAccount(mort.accountNumber);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = {
      fromSavingsAccountNumber: savingsAccount,
      toMortgageAccountNumber: mortgageAccount,
      mortgageAmount: amount,
      remarks,
    };
    const validationMsg = handleFormValidation(formData);
    if (validationMsg) {
      message.error(validationMsg);
      return;
    }
    if (selectedSavings && parseFloat(amount) > selectedSavings.balance) {
      message.error('Insufficient balance in savings account.');
      return;
    }

    setSubmitting(true);
    try {
      await mockDataService.postTransfer({
        amount: parseFloat(amount),
        remainingBalance: (selectedMortgage?.balance || 0) - parseFloat(amount),
        mortgageAccount: {
          accountNumber: mortgageAccount,
          accountType: 'MORTGAGE',
          accountName: selectedMortgage?.customerName || 'Housing Loan',
        },
        savingsAccount: {
          accountNumber: savingsAccount,
          accountType: 'SAVINGS',
          accountName: selectedSavings?.customerName || 'Savings',
        },
      });
      message.success('Transfer successful!');
      navigate('/success-transfer');
    } catch {
      message.error('Transfer failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingAccounts) {
    return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spin size="large" /></div>;
  }

  return (
    <div className="transfer-wrapper">
      <Card className="transfer-card" bodyStyle={{ padding: '32px 36px' }}>
        <div style={{ marginBottom: 24 }}>
          <Title level={4} style={{ margin: 0, color: '#1C2B4A' }}>Mortgage Fund Transfer</Title>
          <Text type="secondary" style={{ fontSize: 13 }}>
            Transfer funds from your savings to reduce your mortgage balance.
          </Text>
        </div>
        <Divider style={{ margin: '0 0 24px' }} />

        <Form layout="vertical" onSubmitCapture={handleSubmit} className="transfer-form-inner">
          <Form.Item label={<span className="form-label">From Savings Account</span>}>
            <Select
              value={savingsAccount}
              onChange={setSavingsAccount}
              size="large"
              style={{ width: '100%' }}
            >
              {savingsAccounts.map((a) => (
                <Option key={a.accountNumber} value={a.accountNumber}>
                  ••••{a.accountNumber.slice(-4)} — ${a.balance.toLocaleString()}
                </Option>
              ))}
            </Select>
            {selectedSavings && (
              <div className="account-balance-hint">
                Available: <strong>${selectedSavings.balance.toLocaleString('en-US', { minimumFractionDigits: 2 })}</strong>
              </div>
            )}
          </Form.Item>

          <Form.Item label={<span className="form-label">To Mortgage Account</span>}>
            <Select
              value={mortgageAccount}
              onChange={setMortgageAccount}
              size="large"
              style={{ width: '100%' }}
            >
              {mortgageAccounts.map((a) => (
                <Option key={a.accountNumber} value={a.accountNumber}>
                  ••••{a.accountNumber.slice(-4)} — Outstanding: ${a.balance.toLocaleString()}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item label={<span className="form-label">Transfer Amount ($)</span>}>
            <Input
              size="large"
              type="number"
              min="1"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              prefix={<span style={{ color: '#64748B', fontWeight: 600 }}>$</span>}
            />
          </Form.Item>

          <Form.Item label={<span className="form-label">Remarks</span>}>
            <TextArea
              rows={3}
              placeholder="Enter a brief description (10–100 characters)"
              value={remarks}
              onChange={(e) => setRemarks(e.target.value)}
              maxLength={LIMITS.REMARKS_LIMIT}
              showCount
            />
          </Form.Item>

          <div className="transfer-btn-row">
            <Button
              htmlType="submit"
              size="large"
              loading={submitting}
              icon={<SendOutlined />}
              style={{ background: '#1C2B4A', color: '#fff', borderColor: '#1C2B4A', fontWeight: 600, flex: 1 }}
            >
              Submit Transfer
            </Button>
            <Button
              size="large"
              icon={<ReloadOutlined />}
              onClick={handleReset}
              style={{ fontWeight: 600 }}
            >
              Reset
            </Button>
          </div>
        </Form>
      </Card>
    </div>
  );
};

export default TransferForm;
